import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ArrowLeft } from "lucide-react";
import { api } from "../../lib/api";
import ImageField from "../../components/admin/ImageField";
import Select from "../../components/Select";

const SPECIALTIES = ["Wedding Planning", "Bridal Couture", "Photography", "Makeup and Beauty", "Choreography", "Music and DJs", "Content Creators", "Decor", "Floral", "Brand Partners"];
const EMPTY = { name: "", specialty: "Wedding Planning", city: "", description: "", image: "", instagram_url: "", website_url: "", featured: false };

export default function VendorsTab() {
  const [vendors, setVendors] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api("/vendors").then(setVendors).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api(`/admin/vendors/${editing.id}`, { method: "PUT", body: editing });
        toast.success("Vendor updated");
      } else {
        await api("/admin/vendors", { method: "POST", body: editing });
        toast.success("Vendor added to the directory");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Remove "${v.name}" from the directory?`)) return;
    try {
      await api(`/admin/vendors/${v.id}`, { method: "DELETE" });
      toast.success("Vendor removed");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (editing) {
    const set = (k, v) => setEditing({ ...editing, [k]: v });
    return (
      <div data-testid="vendor-editor">
        <button onClick={() => setEditing(null)} data-testid="vendor-back-button" className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-ink/60 hover:text-gold font-semibold">
          <ArrowLeft size={14} /> All Vendors
        </button>
        <h2 className="font-display text-3xl text-ink mt-4">{editing.id ? "Edit Vendor" : "New Vendor"}</h2>

        <div className="mt-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Name</label>
              <input data-testid="vendor-name-input" value={editing.name} onChange={(e) => set("name", e.target.value)}
                className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Specialty</label>
              <Select testid="vendor-specialty-select" value={editing.specialty} onChange={(v) => set("specialty", v)} options={SPECIALTIES} />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">City</label>
              <input data-testid="vendor-city-input" value={editing.city} onChange={(e) => set("city", e.target.value)}
                className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Instagram URL</label>
              <input data-testid="vendor-instagram-input" value={editing.instagram_url} onChange={(e) => set("instagram_url", e.target.value)}
                className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Website URL</label>
            <input data-testid="vendor-website-input" value={editing.website_url} onChange={(e) => set("website_url", e.target.value)}
              className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white" />
          </div>
          <ImageField label="Photo" value={editing.image} onChange={(v) => set("image", v)} testid="vendor-image-input" />
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">About</label>
            <textarea data-testid="vendor-description-input" rows={4} value={editing.description} onChange={(e) => set("description", e.target.value)}
              className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white" />
          </div>
          <label className="flex items-center gap-3 text-sm text-ink/80">
            <input type="checkbox" data-testid="vendor-featured-toggle" checked={editing.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#B89742]" />
            Featured listing
          </label>
          <button onClick={save} data-testid="vendor-save-button" disabled={!editing.name}
            className="bg-ink text-cream px-8 py-3.5 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-gold transition-colors duration-300 disabled:opacity-40">
            {editing.id ? "Save Changes" : "Add Vendor"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="vendors-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-ink">Vendor Directory</h2>
          <p className="mt-2 text-sm text-neutral-500">The artisans listed on the public directory page.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="vendor-new-button"
          className="flex items-center gap-2 bg-ink text-cream px-6 py-3 text-[10px] uppercase tracking-[0.25em] font-semibold hover:bg-gold transition-colors duration-300">
          <Plus size={14} /> New Vendor
        </button>
      </div>

      <div className="mt-8 divide-y divide-gold/15 border-y border-gold/15">
        {!vendors ? (
          <p className="py-6 font-display italic text-gold">Loading</p>
        ) : vendors.length === 0 ? (
          <p className="py-6 text-sm text-neutral-500">No vendors yet.</p>
        ) : (
          vendors.map((v) => (
            <div key={v.id} data-testid={`vendor-row-${v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="py-5 flex items-center gap-5">
              {v.image && <img src={v.image} alt="" className="w-16 h-16 object-cover shrink-0 hidden sm:block" />}
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg text-ink truncate">{v.name}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50 mt-1">
                  {v.specialty}{v.city ? ` · ${v.city}` : ""}{v.featured ? " · Featured" : ""}
                </p>
              </div>
              <button onClick={() => setEditing({ ...EMPTY, ...v })} data-testid={`vendor-edit-${v.id}`} aria-label="Edit"
                className="w-10 h-10 border border-ink/15 flex items-center justify-center text-ink/60 hover:border-gold hover:text-gold transition-colors duration-300">
                <Pencil size={15} />
              </button>
              <button onClick={() => remove(v)} data-testid={`vendor-delete-${v.id}`} aria-label="Delete"
                className="w-10 h-10 border border-ink/15 flex items-center justify-center text-ink/60 hover:border-red-400 hover:text-red-500 transition-colors duration-300">
                <Trash2 size={15} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
